import React, { useEffect, useRef, useState } from 'react';
import { useForm, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { Query } from '@apollo/client/react/components';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { OperationVariables, QueryResult } from '@apollo/client';
import {
  setAdriverSelectedCity,
  setCategoryIds,
  setChosenLocationNames,
  setCurrentCity,
  setCurrentState,
  setLocationIds,
  setResetButtonIsDisabled
} from '../../../actions';
import { Condition } from '../common/Condition';
import searchFormConditionsValues from '../../../defaultData/searchFormConditionsValues';
import { NewSearchArea } from './components/NewSearchArea';
import { NewSearchRooms } from './components/NewSearchRooms';
import { NewSearchPrice } from './components/NewSearchPrice';
import { NewSearchFloors } from './components/NewSearchFloors';
import { NewLandAreaFields } from './components/NewLandAreaFields';
import { NewSearchCategory } from './components/NewSearchCategory';
import { NewRentalPeriod } from './components/NewRentalPeriod';
import { NewSearchRepairType } from './components/NewSearchRepairType';
import { NewSearchOnlyResidences } from './components/NewSearchOnlyResidences';
import { NewSearchBuildingType } from './components/NewSearchBuildingType';
import { NewSearchNewOrSecondaryEstate } from './components/NewOrSecondaryEstate';
import { NewSearchAdNumber } from './components/NewSearchAdNumber';
import { checkIsFlatCategory } from '../../../utils/helpers/common';
import { Loader } from '../../ui/Button/loader.styled';
import {
  ANY_CITY_ID,
  BUILDING_TYPE_ID,
  Cities
} from '../../../utils/helpers/constants';
import { Modal } from '../../ui/Modal';
import { NewSearchFiltersProps } from './types';
import {
  selectSearchFormResetButtonIsDisabled,
  selectSearchFormResultByNumber
} from '../../../store/selectors';
import { FilterContainer, modalFooterStyles, modalStyles } from './styled';
import { MainFilters } from './components/MainFilters';
import { searchTotalCountQuery } from '../../../api/queries';
import { BackButtonIconEnum } from '../../ui/PageHeader/types';
import useScreenSize from '../../../hooks/useScreenSize';

export const NewSearchFilters = ({
  isOpen,
  hide,
  undo,
  formEl,
  modalParams,
  searchVariables
}: NewSearchFiltersProps) => {
  const dispatch = useDispatch();
  const renderProps = useFormState();
  const router = useRouter();
  const { t } = useTranslation();
  const { submit } = useForm();
  const { isSmall } = useScreenSize();

  const screen = modalParams?.initialScreen;
  const resetButtonIsDisabled = useSelector(
    selectSearchFormResetButtonIsDisabled
  );
  const resultByNumber = useSelector(selectSearchFormResultByNumber);
  const [citiesPopupIsOpen, setCitiesPopupIsOpen] = useState(screen === 'city');
  const [locationsPopupIsOpen, setLocationsPopupIsOpen] = useState(
    screen === 'locations'
  );

  const categoryFlat = checkIsFlatCategory(renderProps.values.category_id);
  const newSearchNewOrSecondaryEstateRef = useRef<{
    getCategoryIdsLocal: () => void;
  } | null>(null);
  const [initialCity, setInitialCity] = useState<undefined | string>();

  useEffect(() => {
    setInitialCity(renderProps.values.city_id);
    setLocationsPopupIsOpen(screen === 'locations');
    setCitiesPopupIsOpen(screen === 'city');
  }, [modalParams]);

  const formResetHandler = () => {
    const resetData = {
      city_id: ANY_CITY_ID,
      leased: 'false',
      category_id: BUILDING_TYPE_ID.FLAT_ALL,
      locations_ids: undefined
    };
    dispatch(setCurrentCity(ANY_CITY_ID));
    formEl.current?.reset(resetData);
    dispatch(setLocationIds([]));
    dispatch(setChosenLocationNames([]));
    dispatch(setCurrentState(resetData));
    dispatch(setAdriverSelectedCity(Cities.Baku.az));
    dispatch(setResetButtonIsDisabled(true));
  };

  const renderSubmitText = (additionalFilters = {}) => {
    const preparedSearchVariables = {
      ...searchVariables,
      filter: { ...searchVariables.filter, ...additionalFilters }
    };

    if (resultByNumber.hasAnyResult) {
      const resultCount = resultByNumber.result ? 1 : 0;
      return t('search.form.show_n_ads_one', {
        count: resultCount
      });
    }

    return (
      <Query
        ssr={false}
        variables={preparedSearchVariables}
        fetchPolicy="cache-and-network"
        query={searchTotalCountQuery}
      >
        {({
          loading,
          data,
          error
        }: QueryResult<
          { itemsConnection: { totalCount: number } },
          OperationVariables
        >) => {
          if (error) {
            return <>{t('search.form.show_ads')}</>;
          }

          const count = data?.itemsConnection?.totalCount || 0;

          return loading || renderProps.submitting ? (
            <Loader />
          ) : (
            <>{t('search.form.show_n_ads_many', { count })}</>
          );
        }}
      </Query>
    );
  };

  const onSubmit = () => {
    dispatch(
      setCategoryIds(
        newSearchNewOrSecondaryEstateRef.current?.getCategoryIdsLocal()
      )
    );
    if (resultByNumber.hasAnyResult) {
      const itemId = resultByNumber.result
        ? resultByNumber.result.id
        : renderProps.values.ad_number;

      return router.push(`/items/${itemId}`);
    }

    submit();
    hide();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={undo}
      modalId="new-search"
      cypress="new-search--filters"
      title={t('search.filters.title')}
      resetIsDisabled={resetButtonIsDisabled}
      onReset={formResetHandler}
      resetText={t('common.reset')}
      confirmText={renderSubmitText()}
      onConfirm={onSubmit}
      footerStyles={modalFooterStyles}
      modalStyles={modalStyles}
      headerIcon={BackButtonIconEnum.Close}
      closeButtonAlign={isSmall ? 'left' : 'right'}
      showHeaderBorder={!isSmall}
    >
      <FilterContainer>
        <MainFilters
          setInitialCity={setInitialCity}
          modalParams={modalParams}
          setLocationsPopupIsOpen={setLocationsPopupIsOpen}
          hide={hide}
          setCitiesPopupIsOpen={setCitiesPopupIsOpen}
          locationsPopupIsOpen={locationsPopupIsOpen}
          initialCity={initialCity}
          citiesPopupIsOpen={citiesPopupIsOpen}
          renderSubmitText={renderSubmitText}
        />

        <NewSearchCategory />
        {categoryFlat && <NewSearchNewOrSecondaryEstate />}
        <Condition
          targetFieldName="category_id"
          acceptedFieldValues={searchFormConditionsValues.building_type}
          fieldNamesToDisplay={['building_type']}
        >
          <NewSearchBuildingType />
        </Condition>
        <NewSearchPrice />
        <Condition
          targetFieldName="category_id"
          acceptedFieldValues={searchFormConditionsValues.room_ids}
          fieldNamesToDisplay={['room_ids']}
        >
          <NewSearchRooms />
        </Condition>
        <Condition
          targetFieldName="category_id"
          acceptedFieldValues={searchFormConditionsValues.has_repair}
          fieldNamesToDisplay={['has_repair']}
        >
          <NewSearchRepairType />
        </Condition>
        <Condition
          targetFieldName="leased"
          acceptedFieldValues={searchFormConditionsValues.paid_daily}
          fieldNamesToDisplay={['paid_daily']}
        >
          <NewRentalPeriod />
        </Condition>
        <NewSearchArea />
        <Condition
          targetFieldName="category_id"
          acceptedFieldValues={searchFormConditionsValues.land_area}
          fieldNamesToDisplay={['land_area_from', 'land_area_to']}
        >
          <NewLandAreaFields />
        </Condition>
        <Condition
          targetFieldName="category_id"
          acceptedFieldValues={searchFormConditionsValues.floors}
          fieldNamesToDisplay={[
            'floor_from',
            'floor_to',
            'floor_first',
            'floor_last'
          ]}
        >
          <NewSearchFloors />
        </Condition>

        <Condition
          targetFieldName="leased"
          acceptedFieldValues={
            searchFormConditionsValues.only_residences.leased
          }
          fieldNamesToDisplay={['only_residences']}
        >
          <Condition
            targetFieldName="category_id"
            acceptedFieldValues={
              searchFormConditionsValues.only_residences.category_id
            }
            fieldNamesToDisplay={['only_residences']}
          >
            <NewSearchOnlyResidences />
          </Condition>
        </Condition>

        <NewSearchAdNumber />
      </FilterContainer>
    </Modal>
  );
};
